/* Created by Interactive 63 - https://interactive63.com */
/**
 * Preparation of a sanitised SVG document for editing: derives the viewBox, removes fixed sizing,
 * assigns stable element IDs and labels, and builds the element table.
 */
import { elementIdFromIndex } from '../model/ids';
import { GEOMETRY_TAGS, type ElementId, type ElementMeta } from '../model/types';

export interface PreparedSvg {
  source: string;
  elements: Record<ElementId, ElementMeta>;
  rootIds: ElementId[];
  viewBox: { x: number; y: number; width: number; height: number };
  originalWidth?: string;
  originalHeight?: string;
  warnings: string[];
}

const ANIMATABLE_TAGS = new Set([
  ...GEOMETRY_TAGS,
  'g',
  'a',
  'use',
  'image',
  'text',
  'tspan',
  'svg',
]);
// Contents of these are referenced by other elements and are not animated.
const SKIPPED_CONTAINERS = new Set(['clippath', 'mask', 'pattern', 'filter', 'marker', 'symbol']);
const GRADIENT_TAGS = new Set(['lineargradient', 'radialgradient']);
const TAG_NAMES: Record<string, string> = { g: 'Group', a: 'Link', svg: 'SVG' };
const DEFAULT_VIEWBOX = { x: 0, y: 0, width: 300, height: 150 };
const MAX_COMFORTABLE_ELEMENTS = 2000;

/** Title-cases a tag name for generated labels. */
function tagTitle(tag: string): string {
  return TAG_NAMES[tag] ?? tag.charAt(0).toUpperCase() + tag.slice(1);
}

/** Parses a width or height attribute, returning its number and whether it had a non-px unit. */
function parseLength(value: string | null): { value: number; foreignUnit: boolean } | null {
  if (value === null) return null;
  const match = /^\s*([+-]?(?:\d+\.?\d*|\.\d+))\s*([a-z%]*)\s*$/i.exec(value);
  if (!match?.[1]) return null;
  const parsed = Number(match[1]);
  if (!(parsed > 0)) return null;
  return {
    value: parsed,
    foreignUnit: (match[2] ?? '') !== '' && match[2]?.toLowerCase() !== 'px',
  };
}

/** Reads the root viewBox, deriving it from width and height when absent. */
function resolveViewBox(
  root: Element,
  warnings: string[],
): { x: number; y: number; width: number; height: number } {
  const numbers = (root.getAttribute('viewBox') ?? '')
    .split(/[\s,]+/)
    .filter((part) => part !== '')
    .map(Number);
  const [x, y, width, height] = numbers;
  if (
    numbers.length === 4 &&
    numbers.every(Number.isFinite) &&
    width !== undefined &&
    height !== undefined &&
    width > 0 &&
    height > 0
  ) {
    return { x: x ?? 0, y: y ?? 0, width, height };
  }

  const w = parseLength(root.getAttribute('width'));
  const h = parseLength(root.getAttribute('height'));
  if (w && h) {
    if (w.foreignUnit || h.foreignUnit) {
      warnings.push(
        'This SVG uses units other than pixels for its size, so the numbers were used as pixels.',
      );
    }
    return { x: 0, y: 0, width: w.value, height: h.value };
  }
  warnings.push('This SVG has no size information, so a default canvas size was used.');
  return { ...DEFAULT_VIEWBOX };
}

/**
 * Prepares a parsed SVG document in place and returns the serialised source with the element
 * table. IDs are assigned depth-first in document order.
 */
export function prepareSvg(doc: Document): PreparedSvg {
  const root = doc.documentElement;
  const warnings: string[] = [];
  const viewBox = resolveViewBox(root, warnings);
  root.setAttribute('viewBox', `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`);

  const originalWidth = root.getAttribute('width') ?? undefined;
  const originalHeight = root.getAttribute('height') ?? undefined;
  root.removeAttribute('width');
  root.removeAttribute('height');

  // IDs are assigned below; any the upload carried could point at the wrong element.
  for (const el of Array.from(root.querySelectorAll('[data-st-id]')))
    el.removeAttribute('data-st-id');
  root.removeAttribute('data-st-id');

  const elements: Record<ElementId, ElementMeta> = {};
  const rootIds: ElementId[] = [];
  const tagCounts: Record<string, number> = {};
  let counter = 0;
  const seen = { text: false };

  /** Picks a label from design-tool names, the element ID, or a generated one. */
  const labelFor = (el: Element, tag: string): string => {
    tagCounts[tag] = (tagCounts[tag] ?? 0) + 1;
    const fromTool = el.getAttribute('data-st-label') ?? el.getAttribute('data-name');
    el.removeAttribute('data-st-label');
    return fromTool?.trim() || el.getAttribute('id') || `${tagTitle(tag)} ${tagCounts[tag]}`;
  };

  /** Registers an element, giving it an ID, label and metadata. */
  const register = (
    el: Element,
    tag: string,
    parentId: ElementId | null,
    label?: string,
  ): ElementId => {
    counter += 1;
    const id = elementIdFromIndex(counter);
    el.setAttribute('data-st-id', id);
    elements[id] = {
      id,
      tag,
      label: label ?? labelFor(el, tag),
      parentId,
      childIds: [],
      origin: { x: 50, y: 50 },
      excludeFromExport: false,
      hiddenInEditor: false,
    };
    if (parentId) elements[parentId]?.childIds.push(id);
    else if (el.parentElement === root) rootIds.push(id);
    return id;
  };

  /** Numbers the stops of a gradient found inside defs. */
  const registerStops = (gradient: Element): void => {
    const name = gradient.getAttribute('data-name') ?? gradient.getAttribute('id') ?? 'Gradient';
    let stopIndex = 0;
    for (const child of Array.from(gradient.children)) {
      if (child.localName !== 'stop') continue;
      stopIndex += 1;
      register(child, 'stop', null, `Stop ${stopIndex} (${name})`);
    }
  };

  /** Walks the tree depth-first, registering animatable elements and gradient stops. */
  const walk = (parent: Element, parentId: ElementId | null): void => {
    for (const el of Array.from(parent.children)) {
      const tag = el.localName.toLowerCase();
      if (SKIPPED_CONTAINERS.has(tag)) continue;
      if (GRADIENT_TAGS.has(tag)) {
        registerStops(el);
        continue;
      }
      if (tag === 'defs') {
        // Only gradient stops inside defs can be animated.
        for (const nested of Array.from(el.querySelectorAll('*'))) {
          if (GRADIENT_TAGS.has(nested.localName.toLowerCase())) registerStops(nested);
        }
        continue;
      }
      if (!ANIMATABLE_TAGS.has(tag)) continue;
      if (tag === 'text') seen.text = true;
      const id = register(el, tag, parentId);
      walk(el, id);
    }
  };
  walk(root, null);

  if (seen.text) {
    warnings.push(
      'This SVG contains live text. It will render in the viewer’s fonts unless the font is available on the page, so consider converting text to outlines.',
    );
  }
  if (counter > MAX_COMFORTABLE_ELEMENTS) {
    warnings.push(
      'This SVG has a lot of elements, so the editor may feel slow. Simplifying it in your design tool will help.',
    );
  }

  return {
    source: new XMLSerializer().serializeToString(root),
    elements,
    rootIds,
    viewBox,
    ...(originalWidth === undefined ? {} : { originalWidth }),
    ...(originalHeight === undefined ? {} : { originalHeight }),
    warnings,
  };
}
